import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Inline, LinkButton, Stack, Text, ValidationMessage } from '@etg/wings';
import type {
  BoundNew,
  PaymentMethodPrice,
  TravelerNew,
  TravelerPrice,
  TripType,
} from '@eti/schema-types';
import { AgeType, Tier } from '@eti/schema-types';
import { useHeightContext, useTranslation } from '@eti/providers';
import { ContentWrapper } from '@eti/components';
import { css, mediaQueries } from '@eti/styles';
import { analytics } from '@eti/utils';
import type { AncillaryBundleOptionType } from '../../../../common/utils/models';
import { saveUpToAmountWithBundle, uniqueFeatures } from '../../../../utils/transformBundleData';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import { PriceInformationModal } from '../price-information/PriceInformationModal';
import type { AvailableExtraProductsType } from '../v3/branded-fares/model';
import EtgFaresSelection from './EtgFaresSelection';
import EtgFaresHeading from './EtgFaresHeading';
import EtgFaresDrawer from './EtgFaresDrawer';
import EtgFaresStickyFooter from './EtgFaresStickyFooter';
import { getIncludedProductPrice } from './utils';

const growContainerStyles = (headerAndStepIndicatorHeight: number) => css`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - ${headerAndStepIndicatorHeight}px);
`;

const sectionWrapperStyles = css`
  margin-bottom: 24px;
  margin-top: 10px;

  @media ${mediaQueries.medium.up} {
    margin-top: 24px;
  }
`;

export const linkButtonStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;

  li {
    display: inline;
  }
`;

export interface ProductBundleProps {
  availableExtraProducts: AvailableExtraProductsType[];
  bounds: BoundNew[];
  discountAmount: number;
  discountPercentage: number;
  handleProceedButtonClick: () => {};
  onSkipStepClick: () => {};
  name: string;
  onChange: (selectedOption: AncillaryBundleOptionType) => void;
  options: AncillaryBundleOptionType[];
  paymentMethodPrices: PaymentMethodPrice[];
  selectedBundleId: string;
  selectedExtraProducts: number[];
  travelerPrices: TravelerPrice[];
  travelerPricesWithoutDiscounts: TravelerPrice[];
  tripTravelers: TravelerNew[];
  tripType: TripType;
}

const stepIndicatorHeight = 45;

const EtgFares = ({
  availableExtraProducts,
  bounds,
  discountAmount,
  discountPercentage,
  handleProceedButtonClick,
  onSkipStepClick,
  name,
  onChange,
  options,
  paymentMethodPrices,
  selectedBundleId,
  selectedExtraProducts,
  travelerPrices,
  travelerPricesWithoutDiscounts,
  tripTravelers,
  tripType,
}: ProductBundleProps) => {
  const { t } = useTranslation();
  const { headerHeightContext } = useHeightContext();
  const headerAndStepIndicatorHeight = headerHeightContext + stepIndicatorHeight;
  const [hasValidationError, setHasValidationError] = useState(false);

  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);
  const validationRef = useRef<HTMLDivElement>(null);

  const etgFaresDetailsDrawerLinkText = t('Product.ETG.Fares.Details.Drawer.Link');
  analytics.useLogMouseFlowTagEffect('ETG bundle');
  const sellableOptions = options.filter((option) => option.type !== Tier.Basic);
  const selectedBundleOption = options.find((option) => option.id === selectedBundleId);

  const travelersWithoutInfants = tripTravelers.filter(
    (traveler) => traveler.ageType !== AgeType.Inf,
  );
  const numberOfTravelersWithoutInfants = travelersWithoutInfants.length;

  const tripPricePerAdult =
    travelerPrices.find((travelerPrice) =>
      travelersWithoutInfants.some(
        (traveler) => traveler.ageType === AgeType.Adt && traveler.id === travelerPrice.travelerId,
      ),
    )?.price?.price?.value || 0;

  const includedInTripProductPrice = getIncludedProductPrice(
    availableExtraProducts,
    selectedExtraProducts,
  );

  const totalTripPrice = travelerPrices.reduce((acc, total) => acc + total.price.price.value, 0);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedOptionId = event.target.value;
    const selectedOption = options.find((option) => option.id === selectedOptionId);

    if (hasValidationError) {
      setHasValidationError(false);
    }
    if (selectedOption) {
      onChange(selectedOption);
    }
  };

  useEffect(() => {
    if (hasValidationError && validationRef.current) {
      validationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        validationRef.current?.focus();
      }, 400);
    }
  }, [hasValidationError]);

  const onProceedClick = () => {
    if (!selectedBundleId) {
      setHasValidationError(true);
    } else {
      handleProceedButtonClick();
    }
  };

  const { isOpen, handleToggle } = useToggle(false);
  const {
    isOpen: isPriceModalOpen,
    handleOpen: handlePriceModalOpen,
    handleClose: handlePriceModalClose,
  } = useToggle();

  const focusSkipButton = () => {
    skipButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    skipButtonRef.current?.focus();
  };

  return (
    <section className={growContainerStyles(headerAndStepIndicatorHeight)} data-testid="etg-fares">
      <ContentWrapper className={sectionWrapperStyles}>
        <Stack spacing={24}>
          <EtgFaresHeading
            discountPercentage={discountPercentage}
            numberOfSellableOptions={sellableOptions.length}
            savedAmount={saveUpToAmountWithBundle(sellableOptions, discountPercentage)}
          />
          <EtgFaresDrawer
            availableExtraProducts={availableExtraProducts}
            bounds={bounds}
            dataTestId="etg-overlay-drawer"
            handleClose={handleToggle}
            isOpen={isOpen}
            sellableOptions={uniqueFeatures(sellableOptions)}
            title={etgFaresDetailsDrawerLinkText}
            tripType={tripType}
          />
          {hasValidationError && (
            <ValidationMessage
              aria-labelledby="etg-fares-validation-message-text"
              data-testid="etg-fares-validation-message"
              ref={validationRef}
              tabIndex={-1}
              variant="error"
            >
              <Inline spacing={8}>
                <Text aria-live="assertive" id="etg-fares-validation-message-text">
                  {t('Product.ETG.Fares.Validation.Message')}
                </Text>
                <ul className={linkButtonStyles}>
                  <li>
                    <LinkButton onClick={() => firstRadioRef.current?.focus()}>
                      {t('Product.ETG.Fares.Validation.SelectOption.Text')}
                    </LinkButton>
                  </li>
                  <li>
                    <LinkButton onClick={focusSkipButton}>
                      {t('YourSelectedTrip.Page.SkipStepButton.Label')}
                    </LinkButton>
                  </li>
                </ul>
              </Inline>
            </ValidationMessage>
          )}

          <EtgFaresSelection
            availableExtraProducts={availableExtraProducts}
            bounds={bounds}
            discountPercentage={discountPercentage}
            firstRadioRef={firstRadioRef}
            handleOnChange={handleOnChange}
            hasValidationError={hasValidationError}
            name={name}
            numberOfTravelersWithoutInfants={numberOfTravelersWithoutInfants}
            sellableOptions={sellableOptions}
          />
          <Inline>
            <LinkButton
              aria-controls="product-etg-info-drawer"
              aria-expanded={isOpen ? 'true' : 'false'}
              aria-haspopup="dialog"
              data-testid={`${name}-drawer-link-content`}
              onClick={handleToggle}
            >
              {etgFaresDetailsDrawerLinkText}
            </LinkButton>
          </Inline>
        </Stack>
      </ContentWrapper>
      <EtgFaresStickyFooter
        handlePriceModalOpen={handlePriceModalOpen}
        includedInTripProductPrice={includedInTripProductPrice}
        numberOfTravelersWithoutInfants={numberOfTravelersWithoutInfants}
        onProceedClick={onProceedClick}
        onSkipStepClick={onSkipStepClick}
        selectedBundlePrice={selectedBundleOption?.priceRaw || 0}
        skipButtonRef={skipButtonRef}
        totalTripPrice={totalTripPrice}
        tripPricePerAdult={tripPricePerAdult}
      />
      <PriceInformationModal
        availableExtraProducts={availableExtraProducts}
        bounds={bounds}
        discountAmount={discountAmount}
        finalProductIdsToPurchase={selectedExtraProducts}
        handleClose={handlePriceModalClose}
        isOpen={isPriceModalOpen}
        paymentMethodPrices={paymentMethodPrices}
        selectedBundleOption={selectedBundleOption}
        travelerPrices={travelerPrices}
        travelerPricesWithoutDiscounts={travelerPricesWithoutDiscounts}
        tripTravelers={tripTravelers}
      />
    </section>
  );
};

export default EtgFares;
